import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiAlertCircle,
  FiRefreshCw,
  FiFileText,
  FiCheckCircle,
} from "react-icons/fi";
import { useFeed } from "../../hooks/useFeed";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import FeedPost from "./FeedPost";
import PostSkeleton from "./PostSkeleton";
import FeedFilters from "./FeedFilters";
import CreatePostBox from "./CreatePost/CreatePostBox";
import "./FeedContainer.css";

// Animation variants for stagger effects
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.98,
    transition: {
      duration: 0.2,
    },
  },
};

const fadeInVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const FeedContainer = () => {
  const [filter, setFilter] = useState(null);
  const [feedType, setFeedType] = useState("personalized");

  const {
    posts,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    addPost,
    updatePost,
    removePost,
    updateFollowStatusForAuthor,
  } = useFeed({ filter, feedType });

  const { lastElementRef } = useInfiniteScroll({
    loading,
    hasMore,
    onLoadMore: loadMore,
  });

  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  // Handle feed type change
  const handleFeedTypeChange = (newType) => {
    setFeedType(newType);
  };

  return (
    <div className="cbx-feed-container">
      {/* Create Post Box */}
      <CreatePostBox onPostCreated={addPost} />

      {/* Feed Filters */}
      <FeedFilters
        feedType={feedType}
        onFeedTypeChange={handleFeedTypeChange}
        filter={filter}
        onFilterChange={handleFilterChange}
      />

      {/* Error State */}
      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            className="cbx-feed-error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
          >
            <div className="cbx-feed-error-content">
              <FiAlertCircle className="cbx-feed-error-icon" />
              <p className="cbx-feed-error-text">{error}</p>
            </div>
            <motion.button
              onClick={refresh}
              className="cbx-retry-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiRefreshCw />
              <span>Try Again</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Feed Posts */}
      <motion.div
        className="cbx-feed-posts"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <AnimatePresence mode="popLayout">
          {posts.map((post, index) => {
            const isLastPost = index === posts.length - 1;

            return (
              <motion.div
                key={post._id}
                ref={isLastPost ? lastElementRef : null}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                layout
              >
                <FeedPost
                  post={post}
                  onUpdate={updatePost}
                  onDelete={removePost}
                  onFollowChange={updateFollowStatusForAuthor}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Loading Skeletons */}
        <AnimatePresence>
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <PostSkeleton />
              <PostSkeleton />
              <PostSkeleton />
            </motion.div>
          )}
        </AnimatePresence>

        {/* End of Feed */}
        <AnimatePresence>
          {!loading && !hasMore && posts.length > 0 && (
            <motion.div
              className="cbx-feed-end"
              variants={fadeInVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <div className="cbx-feed-end-content">
                <span className="cbx-feed-end-icon">
                  <FiCheckCircle size={24} color="var(--cbx-green)" />
                </span>
                <p className="cbx-feed-end-text">You're all caught up!</p>
                <p className="cbx-feed-end-subtext">
                  Check back later for new posts
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        <AnimatePresence>
          {!loading && posts.length === 0 && !error && (
            <motion.div
              className="cbx-feed-empty"
              variants={fadeInVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <div className="cbx-feed-empty-icon">
                <FiFileText />
              </div>
              <h3 className="cbx-feed-empty-title">No posts yet</h3>
              <p className="cbx-feed-empty-desc">
                Start by creating your first post or follow researchers to see
                their updates!
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default FeedContainer;
